// A partner's Team page: invite, re-send and remove the people who work under the partner's
// account. Only the partner opens it (App.js guards the route; team members get 403 from the API).
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import AdminPage from '../components/AdminShell';
import Button from '../components/Button';

const EMPTY = { first_name: '', last_name: '', email: '' };

const lastLogin = (iso) => {
  if (!iso) return 'Never';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Never';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

export default function PartnerTeamPage() {
  const { API_URL } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [confirmId, setConfirmId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API_URL}/partner/team`)
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch(() => setError('Could not load your team.'))
      .finally(() => setLoading(false));
  }, [API_URL]);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const add = async (e) => {
    e.preventDefault();
    setError('');
    setStatus('');
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) {
      setError('First name, last name and email are required.');
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(`${API_URL}/partner/team`, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
      });
      setRows((rs) => [...rs, r.data]);
      setForm(EMPTY);
      setStatus(`${r.data.first_name} ${r.data.last_name} added. We emailed them a link to set a password.`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail === 'email_in_use') {
        setError('That email already has a ProZpects account.');
      } else {
        setError('Could not add the team member. Check the details and try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const resend = async (m) => {
    setError('');
    setStatus('');
    try {
      const r = await axios.post(`${API_URL}/partner/team/${m.id}/resend`);
      if (r.data?.sent) setStatus(`Invite sent to ${m.email}.`);
      else setError(`The invite to ${m.email} could not be sent. Try again later.`);
    } catch (err) {
      setError('Could not re-send the invite.');
    }
  };

  const remove = async (m) => {
    setError('');
    setStatus('');
    try {
      await axios.delete(`${API_URL}/partner/team/${m.id}`);
      setRows((rs) => rs.map((r) => (r.id === m.id ? { ...r, is_active: false } : r)));
      setStatus(`${m.first_name} ${m.last_name} removed.`);
    } catch (err) {
      setError('Could not remove the team member.');
    } finally {
      setConfirmId(null);
    }
  };

  const field = (id, label, key, type = 'text') => (
    <div className="form-field">
      <label className="form-field__label" htmlFor={id}>{label}</label>
      <input id={id} className="form-field__input" type={type} value={form[key]} onChange={set(key)} />
    </div>
  );

  return (
    <AdminPage
      active="team"
      title="Team"
      subtitle="People who work under your account. They see what you see and verify as you."
    >
      {status && <div role="status" className="settings-toast">{status}</div>}
      {error && <div role="alert" className="settings-toast settings-toast--error">{error}</div>}

      <div className="adm__card">
        {loading ? (
          <div className="adm-loading">Loading…</div>
        ) : rows.length === 0 ? (
          <p>No team members yet. Add one below.</p>
        ) : (
          <table className="adm-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Status</th>
                <th>Last login</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => (
                <tr key={m.id}>
                  <td>{m.first_name} {m.last_name}</td>
                  <td>{m.email}</td>
                  <td>{m.is_active ? 'Active' : 'Removed'}</td>
                  <td>{lastLogin(m.last_login_at)}</td>
                  <td>
                    {m.is_active && (confirmId === m.id ? (
                      <>
                        <span>Remove {m.first_name}? </span>
                        <Button variant="accent" onClick={() => remove(m)}>Yes, remove</Button>
                        <Button onClick={() => setConfirmId(null)}>Cancel</Button>
                      </>
                    ) : (
                      <>
                        <Button onClick={() => resend(m)}>Resend invite</Button>
                        <Button onClick={() => setConfirmId(m.id)}>Remove</Button>
                      </>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <form className="adm__card" onSubmit={add}>
        {field('pt-first', 'First name', 'first_name')}
        {field('pt-last', 'Last name', 'last_name')}
        {field('pt-email', 'Email', 'email', 'email')}
        <Button variant="accent" type="submit" disabled={saving}>
          {saving ? 'Adding…' : 'Add team member'}
        </Button>
      </form>
    </AdminPage>
  );
}
